import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Instagram, Facebook } from "lucide-react";
import { BUSINESS, CATEGORIES } from "@/data/content";
import { postNewsletter } from "@/lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const res = await postNewsletter({ email });
      setSent(true);
      toast.success(res.message);
      setEmail("");
    } catch {
      toast.error("Please enter a valid email.");
    }
  };

  return (
    <footer className="bg-wood-900 text-cream" data-testid="site-footer">
      {/* Email signup */}
      <div className="border-b border-cream/10 bg-rose-light/95 text-wood-900">
        <div className="mx-auto grid max-w-7xl gap-8 px-5 py-16 md:grid-cols-2 md:items-center md:px-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-honey-dark">Join the family</p>
            <h2 className="mt-3 font-serif text-4xl leading-tight md:text-5xl">Save 15% on your first order</h2>
            <p className="mt-3 max-w-md text-wood-700">
              Join our email list for a 15% code on your first online order of $20 or more, plus new blends and seasonal gift sets.
            </p>
          </div>
          <form onSubmit={submit} className="flex w-full flex-col gap-3 sm:flex-row" data-testid="newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              data-testid="newsletter-email"
              className="w-full rounded-full border border-wood-900/20 bg-cream px-6 py-4 text-wood-900 outline-none focus:border-honey-dark"
            />
            <button
              type="submit"
              data-testid="newsletter-submit"
              className="whitespace-nowrap rounded-full bg-wood-900 px-8 py-4 font-medium text-cream transition-colors hover:bg-honey-dark"
            >
              {sent ? "Subscribed" : "Subscribe"}
            </button>
          </form>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 md:grid-cols-4 md:px-8">
        <div className="md:col-span-1">
          <span className="font-serif text-2xl">Washington Family</span>
          <p className="mt-1 text-xs uppercase tracking-[0.35em] text-honey">Provisions</p>
          <p className="mt-4 max-w-xs text-sm text-cream/70">
            Premium honey, loose-leaf tea, and botanical body care — made in small batches in {BUSINESS.address}.
          </p>
          <div className="mt-5 flex gap-3">
            <a href={BUSINESS.instagram} target="_blank" rel="noreferrer" data-testid="social-instagram" aria-label="Instagram" className="rounded-full border border-cream/20 p-2 transition-colors hover:bg-cream hover:text-wood-900">
              <Instagram size={18} />
            </a>
            <a href={BUSINESS.facebook} target="_blank" rel="noreferrer" data-testid="social-facebook" aria-label="Facebook" className="rounded-full border border-cream/20 p-2 transition-colors hover:bg-cream hover:text-wood-900">
              <Facebook size={18} />
            </a>
          </div>
        </div>

        <div>
          <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-cream/50">Shop</h4>
          <ul className="mt-4 space-y-2 text-sm">
            {CATEGORIES.map((c) => (
              <li key={c.slug}>
                <Link to={`/shop/${c.slug}`} className="text-cream/80 transition-colors hover:text-honey">
                  {c.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-cream/50">Help</h4>
          <ul className="mt-4 space-y-2 text-sm">
            <li><Link to="/policies/shipping" className="text-cream/80 hover:text-honey">Shipping Policy</Link></li>
            <li><Link to="/policies/returns" className="text-cream/80 hover:text-honey">Returns & Refunds</Link></li>
            <li><Link to="/policies/privacy" className="text-cream/80 hover:text-honey">Privacy Policy</Link></li>
            <li><Link to="/policies/terms" className="text-cream/80 hover:text-honey">Terms of Service</Link></li>
            <li><Link to="/contact" className="text-cream/80 hover:text-honey">Contact</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-cream/50">Contact</h4>
          <ul className="mt-4 space-y-2 text-sm text-cream/80">
            <li><a href={`mailto:${BUSINESS.email}`} className="hover:text-honey">{BUSINESS.email}</a></li>
            <li><a href={`tel:${BUSINESS.phone}`} className="hover:text-honey">{BUSINESS.phone}</a></li>
            <li>{BUSINESS.address}</li>
          </ul>
        </div>
      </div>

      <div className="border-t border-cream/10 py-6 text-center text-xs text-cream/50">
        © {new Date().getFullYear()} Washington Family Provisions · Secure checkout by Square
      </div>
    </footer>
  );
}
